import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import PublicLayout from "@/components/layout/PublicLayout";
import api from "@/lib/api";
import { CheckCircle2, Clock, XCircle } from "lucide-react";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const registrationId = params.get("registration_id");

  const [status, setStatus] = useState("checking"); // checking | paid | pending | failed
  const [payment, setPayment] = useState(null);

  const poll = useCallback(
    async (attempt = 0) => {
      if (attempt >= 10) {
        setStatus("pending");
        return;
      }
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setPayment(data);
        if (data.status === "paid" || data.payment_status === "paid") {
          setStatus("paid");
          return;
        }
        if (data.status === "expired" || data.status === "failed") {
          setStatus("failed");
          return;
        }
      } catch (e) {
        // continue polling
      }
      setTimeout(() => poll(attempt + 1), 2000);
    },
    [sessionId]
  );

  useEffect(() => {
    if (!sessionId) {
      setStatus("failed");
      return;
    }
    poll(0);
  }, [sessionId, poll]);

  return (
    <PublicLayout>
      <section className="min-h-[calc(100vh-200px)] bg-[#F8F9FA] flex items-center justify-center py-16 px-6">
        <div className="w-full max-w-xl bg-white border border-slate-200 p-10 rounded-sm text-center">
          {status === "checking" && (
            <>
              <Clock className="w-16 h-16 mx-auto text-[#0A192F]" />
              <h1 data-testid="payment-status-checking" className="font-display text-3xl uppercase tracking-tighter mt-6">
                Confirming Payment…
              </h1>
              <p className="text-sm text-slate-600 mt-3">
                Please don't close this page. We're verifying your payment with Stripe.
              </p>
            </>
          )}
          {status === "paid" && (
            <>
              <CheckCircle2 className="w-16 h-16 mx-auto text-[#10B981]" />
              <h1 data-testid="payment-status-paid" className="font-display text-3xl uppercase tracking-tighter mt-6">
                Registration Confirmed
              </h1>
              <p className="text-sm text-slate-600 mt-3">
                Your player is officially registered. A confirmation receipt and event details are now available in
                your parent dashboard.
              </p>
              <div className="mt-8 grid grid-cols-2 gap-3">
                <Link to="/dashboard" className="bg-[#0A192F] text-white py-3 text-sm font-semibold uppercase tracking-wider rounded-sm">
                  Go to Dashboard
                </Link>
                <Link to="/camps" className="border border-slate-300 py-3 text-sm font-semibold uppercase tracking-wider rounded-sm">
                  Browse More Camps
                </Link>
              </div>
            </>
          )}
          {status === "pending" && (
            <>
              <Clock className="w-16 h-16 mx-auto text-[#F59E0B]" />
              <h1 className="font-display text-3xl uppercase tracking-tighter mt-6">Still Processing</h1>
              <p className="text-sm text-slate-600 mt-3">
                Your payment is still being processed by Stripe. We'll update your registration as soon as it
                clears.
              </p>
              <Link to="/dashboard" className="inline-block mt-6 px-6 py-3 bg-[#0A192F] text-white text-sm font-semibold uppercase tracking-wider rounded-sm">
                View Dashboard
              </Link>
            </>
          )}
          {status === "failed" && (
            <>
              <XCircle className="w-16 h-16 mx-auto text-[#E63946]" />
              <h1 data-testid="payment-status-failed" className="font-display text-3xl uppercase tracking-tighter mt-6">
                Payment Not Completed
              </h1>
              <p className="text-sm text-slate-600 mt-3">
                Your payment session expired or was canceled. Your registration is still saved as pending — you can
                retry payment from your dashboard.
              </p>
              <Link to="/dashboard" className="inline-block mt-6 px-6 py-3 bg-[#0A192F] text-white text-sm font-semibold uppercase tracking-wider rounded-sm">
                Go to Dashboard
              </Link>
            </>
          )}

          {payment && status === "paid" && (
            <div className="mt-8 border-t border-slate-200 pt-6 text-left text-sm space-y-2">
              <div className="flex justify-between"><span className="label-overline">Amount</span><span className="font-data">${payment.amount} {payment.currency?.toUpperCase()}</span></div>
              <div className="flex justify-between"><span className="label-overline">Transaction</span><span className="font-mono text-xs">{payment.stripe_session_id?.slice(-12)}</span></div>
            </div>
          )}
        </div>
      </section>
    </PublicLayout>
  );
}
